import React, { useState, useEffect, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { api } from "../../../../../../../../utility/AxiosInceptor";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "./enrollment-chart.css";
import CommonLoaderBlue from "../../../../../../../components/Loader/LoaderBlue";
import EmptyChartState from "../../EmptyStateScreens/ChartEmptyState/ChartEmptyState";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const EnrollmentChart = ({
  setIsGlobalChartLoading,
  setShowEmptyState,
  currentModuleSelect,
  allEnrollmentsCount,
  setAllEnrollmentsCount,
  setAllChartData,
  shortSelection,
  selectedRange,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [chartLabels, setChartLabels] = useState([]);
  const [dataChart, setDataChart] = useState([]);
  const [isFirstTimeCalled, setIsFirstTimeCalled] = useState(false);
  const [remBase, setRemBase] = useState(
    parseFloat(getComputedStyle(document.documentElement).fontSize)
  );
  const chartRef = useRef(null);

  // Helper to convert rem → px dynamically
  const getRemValue = (rem) => rem * remBase;

  useEffect(() => {
    const handleResize = () => {
      setRemBase(
        parseFloat(getComputedStyle(document.documentElement).fontSize)
      );
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchDataAndInitializeChart = () => {
    let accountType = [];
    if (currentModuleSelect.prepaid) accountType.push("Prepaid");
    if (currentModuleSelect.postpaid) accountType.push("Postpaid");
    if (currentModuleSelect.acp) accountType.push("Acp");
    accountType = accountType.join(",");

    api
      .get(
        `/api/web/dashboard/enrollmentStatsByStatus?accountType=${accountType}`,
        {
          params: {
            startDate: moment(selectedRange[0])?.isValid()
              ? moment(selectedRange[0])?.format("MM-DD-YYYY")
              : null,
            endData: moment(selectedRange[1])?.isValid()
              ? moment(selectedRange[1])?.format("MM-DD-YYYY")
              : null,
          },
        }
      )
      .then((res) => {
        const labels = [];
        const data = [];

        Object.keys(res?.data?.result?.statsByStatus).forEach((key) => {
          if (key === "active") {
            labels.push("Active");
            data.push(res.data.result.statsByStatus[key]);
          } else if (key === "suspended") {
            labels.push("Suspended");
            data.push(res.data.result.statsByStatus[key]);
          }
          if (key === "prospect") {
            labels.push("Prospect");
            data.push(res.data.result.statsByStatus[key]);
          }
          if (key === "disconnected") {
            labels.push("Disconnected");
            data.push(res.data.result.statsByStatus[key]);
          }
          if (key === "suspect") {
            labels.push("Suspect");
            data.push(res.data.result.statsByStatus[key]);
          }
        });

        if (!isFirstTimeCalled && res?.data?.result?.totalEnrollments === 0) {
          setShowEmptyState(true);
        }

        setAllChartData(res.data.result.statsByStatus);
        setAllEnrollmentsCount(res?.data?.result?.totalEnrollments);
        setChartLabels(labels);
        setIsFirstTimeCalled(true);
        setDataChart(data);

        setTimeout(() => {
          setIsLoading(false);
          setIsGlobalChartLoading(false);
        }, 500);
      })
      .catch((err) => {
        console.error(err);
        if (err?.response?.data?.msg === "No customers found.") {
          setChartLabels([]);
          setDataChart([]);
        }
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchDataAndInitializeChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortSelection, currentModuleSelect, selectedRange]);

  const data = {
    labels: chartLabels,
    datasets: [
      {
        label: "Enrollment",
        data: dataChart,
        backgroundColor: [
          "#27AE60",
          "#4A90E2",
          "#F39C12",
          "#E74C3C",
          "#95A5A6",
        ],
        borderRadius: getRemValue(0.8), // 0.8rem ≈ 8px
        borderSkipped: false,
        barThickness: getRemValue(8), // 8rem ≈ 80px
        roundCap: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: getRemValue(2), // 2rem ≈ 20px
        right: 0,
        bottom: 0,
        left: 0,
      },
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
    },
    backgroundColor: "#FFFFFF",
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        border: false,
        ticks: {
          font: { size: getRemValue(1.2), family: "Inter", weight: 500 },
          color: "#72728F",
          maxRotation: 0,
          minRotation: 0,
          padding: 0,
          autoSkip: true,
        },
        offset: true,
      },
      y: {
        grid: { display: false, drawBorder: false },
        display: false,
        border: false,
        ticks: {
          font: { size: getRemValue(1.2), family: "Inter", weight: 500 },
          color: "#72728F",
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        titleFont: { family: "Inter", size: getRemValue(1.4), weight: "600" },
        bodyFont: { family: "Inter", size: getRemValue(1.3), weight: "500" },
        footerFont: { family: "Inter", size: getRemValue(1.2) },
        callbacks: { label: (context) => `Customers ${context.raw}` },
      },
      datalabels: {
        anchor: "end",
        align: "end",
        offset: -getRemValue(0.2), // -0.2rem ≈ -2px
        color: "#8B8BA7",
        font: {
          family: "Space Grotesk",
          size: getRemValue(1.4),
          weight: "700",
        },
        formatter: (value) => value,
      },
    },
  };

  return (
    <div className="carrier-chart-main">
      {!isLoading && allEnrollmentsCount === 0 ? (
        <EmptyChartState frominner={true} />
      ) : (
        <>
          <h1>
            Total Enrollments &nbsp;&nbsp;&nbsp;
            <span>{allEnrollmentsCount}</span>
          </h1>
          <div className="carrier-chart-main-container">
            {isLoading ? (
              <div className="flex flex-wrap flex-row justify-content-center align-items-center w-full h-full">
                <div style={{ marginTop: `${getRemValue(1.9)}px` }}>
                  <CommonLoaderBlue sizehere={20} color={"rgb(78, 105, 251)"} />
                </div>
              </div>
            ) : (
              <Bar ref={chartRef} data={data} options={options} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EnrollmentChart;
